import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Target, TrendingUp, BookOpen, Shield, Calendar, ArrowLeft, Settings } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { ISOProgressionPath } from '../components/training/ISOProgressionPath';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';
import { standards } from '../data/standards/standardsData';

export const IATF16949LeadAuditor = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "IATF 16949 Lead Auditor Course | Automotive Quality Management System Certification",
    "description": "Professional IATF 16949 Lead Auditor Course for automotive quality auditors. Globally recognized AQMS lead auditor certification covering system, process, and product audits, Core Tools, and customer-specific requirements.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://www.isde.academy"
    },
    "coursePrerequisites": "Automotive quality management or internal auditing experience recommended",
    "educationalLevel": "Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": ["In-person", "Online"],
      "duration": "5 days"
    }
  };

  const courseCompetencies = [
    'Lead automotive QMS audit teams for system, process, and product audits',
    'Evaluate Core Tools implementation — APQP, PPAP, FMEA, SPC, MSA',
    'Verify customer-specific requirements (CSR) and OEM compliance',
    'Drive zero-defect quality culture and continual improvement across the supply chain'
  ];

  const targetAudience = [
    'Automotive Quality Managers and Quality Engineers',
    'Supplier Quality Development Professionals',
    'IATF 16949 Management Representatives',
    'Production and Process Engineers in Automotive Manufacturing',
    'Certification Body Auditors for Automotive Sector',
    'Automotive Consultants and Advisors',
    'Graduates aspiring to become IATF 16949 lead auditors'
  ];

  const professionalBenefits = [
    {
      title: 'Globally recognized automotive quality lead auditor qualification',
      icon: Award
    },
    {
      title: 'Career advancement in OEM supply chains and automotive quality roles',
      icon: TrendingUp
    },
    {
      title: 'Enhanced credibility as an IATF 16949 and Core Tools expert',
      icon: Shield
    }
  ];

  const organizationalBenefits = [
    {
      title: 'Ability to lead system, process, and product audits across the supply chain',
      icon: Users
    },
    {
      title: 'Improved process capability, defect prevention, and waste reduction',
      icon: Target
    },
    {
      title: 'Reduced warranty costs, customer complaints, and field returns',
      icon: CheckCircle
    }
  ];

  const businessBenefits = [
    {
      title: 'Supports OEM customer satisfaction and strategic supplier development',
      icon: Settings
    },
    {
      title: 'Enables access to global automotive supply chains and OEM qualification',
      icon: Award
    },
    {
      title: 'Competitive advantage in IATF 16949 certification and surveillance',
      icon: CheckCircle
    }
  ];

  const careerOpportunities = [
    'IATF 16949 Lead Auditor',
    'Automotive Quality Consultant',
    'Supplier Quality Engineer',
    'Certification Body Auditor',
    'Automotive QMS Manager',
    'Process Improvement Specialist'
  ];

  const industries = [
    'Automotive OEMs',
    'Tier 1 Suppliers',
    'Tier 2 & Tier 3 Suppliers',
    'Commercial Vehicle Manufacturers',
    'Automotive Electronics',
    'Component Manufacturers',
    'Assembly & Sub-assembly Operations'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="IATF 16949 Lead Auditor Course | Automotive Quality Management Certification | ISDE Academy"
        description="Earn IATF 16949 Lead Auditor certification with ISDE Academy. 5-day expert-led automotive QMS lead auditor course covering system, process and product audits, Core Tools verification, CSR compliance, and defect prevention. Advance your automotive quality career."
        keywords="IATF 16949 lead auditor, automotive QMS lead auditor certification, IATF 16949 auditor course, automotive quality lead auditor India, IATF 16949 certification training, automotive core tools auditor, process audit IATF, automotive supply chain auditor"
        canonical="/IATF-16949-Lead-Auditor-Course"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'ISO Training Services', path: '/training/iso-training-services' },
          { name: 'IATF 16949 Lead Auditor', path: '/IATF-16949-Lead-Auditor-Course' }
        ]}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-gray-700 to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <Settings className="h-5 w-5" />
              AUTOMOTIVE QUALITY MANAGEMENT SYSTEMS
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              IATF 16949 Lead Auditor Course
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Become a globally recognized IATF 16949 Lead Auditor. Master automotive QMS audits, Core Tools verification, and supply chain quality management to drive zero-defect excellence.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>5 Days Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-6 w-6" />
                <span>OEM Recognized</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Back Button */}
      <section className="bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <Button
            onClick={() => navigate('/training/iso-training-services')}
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to ISO Training Services
          </Button>
        </div>
      </section>

      {/* ISO Progression Path */}
      <ISOProgressionPath standard={standards['iatf-16949']} currentLevel="leadAuditor" />

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              The IATF 16949 Lead Auditor Course is a globally recognized professional training programme designed to develop competent automotive quality auditors capable of planning, leading, and managing audits of Automotive Quality Management Systems in accordance with IATF 16949:2016. As automotive OEMs and Tier 1 suppliers increasingly require IATF 16949 certification as a supply chain prerequisite, the demand for qualified lead auditors who understand Core Tools, customer-specific requirements (CSRs), and process audit methodologies has never been higher.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              This 5-day intensive course equips automotive quality professionals with advanced audit leadership skills, comprehensive knowledge of IATF 16949 requirements, Core Tools application (APQP, PPAP, FMEA, SPC, MSA), VDA 6.3 process audit techniques, and internationally recognized lead auditor credentials — enabling them to lead system, process, and product audits that strengthen supply chain quality and support continual improvement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is IATF 16949 Lead Auditor Course Section */}
      <section className="py-16 bg-gradient-to-br from-gray-100 to-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Is the IATF 16949 Lead Auditor Course?
            </h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              The IATF 16949 Lead Auditor Course provides comprehensive training in conducting first-party, second-party, and third-party audits of Automotive QMS based on IATF 16949:2016. Participants learn to plan system audits, execute VDA 6.3 process audits, conduct product audits, verify Core Tools implementation, assess customer-specific requirements compliance, and lead audit teams across complex, multi-site automotive supply chains.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card className="h-full border-2 border-gray-300 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    Certified participants gain competence to:
                  </h3>
                  <div className="space-y-3">
                    {courseCompetencies.map((competency, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle className="h-6 w-6 text-gray-700 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{competency}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex items-center justify-center"
            >
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-32 h-32 rounded-full bg-gradient-to-br from-gray-700 to-slate-800 shadow-2xl mb-4">
                  <Settings className="h-16 w-16 text-white" />
                </div>
                <p className="text-lg font-semibold text-gray-800">
                  Certified Automotive Quality<br />Lead Auditor
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Attend Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Who Should Attend the IATF 16949 Lead Auditor Course?
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              This course is ideal for automotive quality professionals seeking advanced lead auditor credentials, including:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((role, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full border-l-4 border-gray-600 hover:shadow-lg transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-gray-700 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-800">{role}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-gray-100 to-slate-100">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Benefits of IATF 16949 Lead Auditor Course
            </h3>
          </motion.div>

          {/* Professional Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Professional Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {professionalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-200 to-slate-300 mb-4">
                        <benefit.icon className="h-8 w-8 text-gray-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Organizational Benefits */}
          <div className="mb-12">
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Organizational Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {organizationalBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-200 to-slate-300 mb-4">
                        <benefit.icon className="h-8 w-8 text-gray-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Business & Market Benefits */}
          <div>
            <h4 className="text-2xl font-bold text-gray-900 mb-6 text-center">Business & Market Benefits</h4>
            <div className="grid md:grid-cols-3 gap-6">
              {businessBenefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full bg-white hover:shadow-xl transition-all duration-300">
                    <CardContent className="p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-200 to-slate-300 mb-4">
                        <benefit.icon className="h-8 w-8 text-gray-700" />
                      </div>
                      <p className="text-gray-800 font-medium">{benefit.title}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h4 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Career Opportunities After IATF 16949 Lead Auditor Course
            </h4>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Earning IATF 16949 Lead Auditor certification opens significant career opportunities across the global automotive industry:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-12">
            {careerOpportunities.map((career, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Card className="h-full bg-gradient-to-br from-gray-50 to-white border-2 border-gray-300 hover:shadow-lg transition-all">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                      <Award className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-semibold text-gray-900">{career}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Industries Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-gray-700 to-slate-800 rounded-2xl p-8 text-white"
          >
            <h5 className="text-2xl font-bold mb-4 text-center">
              Industries Hiring IATF 16949 Lead Auditors
            </h5>
            <div className="flex flex-wrap justify-center gap-4">
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full font-medium"
                >
                  {industry}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iatf-16949" />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-gray-700 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Become an IATF 16949 Lead Auditor?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join automotive quality professionals worldwide who have advanced their careers with our comprehensive IATF 16949 Lead Auditor certification
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/contact')}
                className="bg-white text-gray-800 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
              >
                Enroll Now
              </Button>
              <Button
                onClick={() => navigate('/training-calendar')}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-gray-800 font-semibold px-8 py-6 text-lg"
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
